import type { BillItemTemplate, CreateBillItemTemplateInput, UpdateBillItemTemplateInput } from '@/types/composition-functions';
import {
  useGetBillItemTemplatesLazyQuery,
  useCreateBillItemTemplateMutation,
  useUpdateBillItemTemplateMutation,
  useDeleteBillItemTemplateMutation,
} from '@/types/vue-apollo-helpers';
import { evictFeeCatalogueQueries } from '@/utils/billingCache';

// Re-export for consumers
export type { BillItemTemplate };

export const useBillItemTemplates = (options: { autoFetch?: boolean } = {}) => {
  const {
    result,
    loading,
    error,
    refetch,
    load,
  } = useGetBillItemTemplatesLazyQuery();
  if (options.autoFetch) load();

  /** First call loads; later calls refetch (a second load() does nothing). */
  const ensureLoaded = () => load() || refetch();

  const search = ref('');

  const templates = computed(() => {
    const items = result.value?.billItemTemplates ?? [];
    if (!search.value) return items;
    const q = search.value.toLowerCase();
    return items.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.billItemCategory?.name?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q),
    );
  });

  // Mutations
  const {
    mutate: createTemplate,
    loading: isCreating,
    onDone: onCreateDone,
    onError: onCreateError,
  } = useCreateBillItemTemplateMutation({
    update: evictFeeCatalogueQueries,
  });

  const {
    mutate: updateTemplate,
    loading: isUpdating,
    onDone: onUpdateDone,
    onError: onUpdateError,
  } = useUpdateBillItemTemplateMutation({
    update: evictFeeCatalogueQueries,
  });

  const {
    mutate: deleteTemplate,
    loading: isDeleting,
    onDone: onDeleteDone,
    onError: onDeleteError,
  } = useDeleteBillItemTemplateMutation({
    update: evictFeeCatalogueQueries,
  });

  /** Fee items that can still be added to a bill (inactive ones are retired). */
  const activeTemplates = computed(() => (result.value?.billItemTemplates ?? []).filter((t) => t.is_active));

  return {
    templates,
    activeTemplates,
    loading,
    error,
    search,
    refetch,
    load,
    ensureLoaded,
    createTemplate: (input: CreateBillItemTemplateInput) => createTemplate({ input }),
    isCreating,
    onCreateDone,
    onCreateError,
    updateTemplate: (input: UpdateBillItemTemplateInput) => updateTemplate({ input }),
    isUpdating,
    onUpdateDone,
    onUpdateError,
    deleteTemplate: (id: string) => deleteTemplate({ id }),
    isDeleting,
    onDeleteDone,
    onDeleteError,
  };
};
